import { ReviewSearchParamsDto } from '@/contracts/marketplace/reviews';
import {
    AiSelectedAnalysisProgress,
    ReviewsAutoReplyProgress,
    ReviewsExportProgress,
} from '@/context/reviews-page.types';
import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { ReviewEntity } from '@/entities/review.entity';
import { Sort } from '@/types/sort.type';
import { Dispatch, SetStateAction } from 'react';

export type ReviewsPageSelectionBindings = {
    selectionSessionId: string;
    pickedReviews: string[];
    pickedProducts: string[];
    pickedBrandGroups: string[];
    setPickedReviews?: (val: string[]) => void;
    setPickedProducts?: (val: string[]) => void;
    setPickedBrandGroups?: (val: string[]) => void;
};

export type ReviewsPageDataBindings = {
    reviews: ReviewEntity[];
    products: ProductGroupEntity[];
    brandGroups: BrandGroupEntity[];
    total: number;
};

export type ReviewsPageTransportBindings = {
    enqueueReviewSelectionMutation: <T>(mutation: () => Promise<T>) => Promise<T>;
    waitForReviewSelectionReconcile: () => Promise<void>;
    getProducts: (abort?: AbortSignal) => Promise<void>;
    getBrandGroups: (abort?: AbortSignal) => Promise<void>;
    refetchReviews?: (abort?: AbortSignal) => Promise<void>;
    isReviewsDataReconciliationRunning?: boolean;
    isReviewsDataReconciliationSelectionVisualRunning?: boolean;
};

export type ReviewsPageQueryBindings = {
    sort: Sort;
    setSort: (sort: Sort) => void;
    refetchReviewsAndProducts: (abort?: AbortSignal) => Promise<void>;
    filters: ReviewSearchParamsDto;
    filtersQuery: string[];
    setFilters: (value: SetStateAction<ReviewSearchParamsDto>) => void;
    reviewsOnPage: number;
    setReviewsOnPage: (count: number) => Promise<void>;
    maxSelectedItems: number | null;
};

export type ReviewsPageProgressBindings = {
    aiAnalysisProgress: AiSelectedAnalysisProgress | null;
    setAiAnalysisProgress: Dispatch<SetStateAction<AiSelectedAnalysisProgress | null>>;
    exportProgress: ReviewsExportProgress | null;
    setExportProgress: Dispatch<SetStateAction<ReviewsExportProgress | null>>;
    autoReplyProgress: ReviewsAutoReplyProgress | null;
    setAutoReplyProgress: Dispatch<SetStateAction<ReviewsAutoReplyProgress | null>>;
};

export type ReviewsPageContextBindings = {
    selection: ReviewsPageSelectionBindings;
    data: ReviewsPageDataBindings;
    query: ReviewsPageQueryBindings;
    transport: ReviewsPageTransportBindings;
    progress: ReviewsPageProgressBindings;
};

export type ReviewsPageContextType = ReviewsPageContextBindings;

export const pickReviewsPageSelectionBindings = ({ selection }: ReviewsPageContextType): ReviewsPageSelectionBindings =>
    selection;

export const pickReviewsPageDataBindings = ({ data }: ReviewsPageContextType): ReviewsPageDataBindings => data;

export const pickReviewsPageQueryBindings = ({ query }: ReviewsPageContextType): ReviewsPageQueryBindings => query;

export const pickReviewsPageTransportBindings = ({ transport }: ReviewsPageContextType): ReviewsPageTransportBindings =>
    transport;

export const pickReviewsPageProgressBindings = ({ progress }: ReviewsPageContextType): ReviewsPageProgressBindings =>
    progress;

export const DEFAULT_REVIEWS_PAGE_CONTEXT_BINDINGS: ReviewsPageContextBindings = {
    selection: {
        selectionSessionId: '',
        pickedReviews: [],
        pickedProducts: [],
        pickedBrandGroups: [],
        setPickedReviews: () => undefined,
        setPickedProducts: () => undefined,
        setPickedBrandGroups: () => undefined,
    },
    data: {
        reviews: [],
        products: [],
        brandGroups: [],
        total: 0,
    },
    query: {
        sort: {},
        setSort: () => undefined,
        refetchReviewsAndProducts: () => Promise.resolve(),
        filters: {},
        filtersQuery: [],
        setFilters: () => undefined,
        reviewsOnPage: 10,
        setReviewsOnPage: () => Promise.resolve(),
        maxSelectedItems: null,
    },
    transport: {
        enqueueReviewSelectionMutation: async <T>(mutation: () => Promise<T>) => mutation(),
        waitForReviewSelectionReconcile: () => Promise.resolve(),
        getProducts: () => Promise.resolve(),
        getBrandGroups: () => Promise.resolve(),
        refetchReviews: () => Promise.resolve(),
        isReviewsDataReconciliationRunning: false,
        isReviewsDataReconciliationSelectionVisualRunning: false,
    },
    progress: {
        aiAnalysisProgress: null,
        setAiAnalysisProgress: (value) => void value,
        exportProgress: null,
        setExportProgress: (value) => void value,
        autoReplyProgress: null,
        setAutoReplyProgress: (value) => void value,
    },
};

export const DEFAULT_REVIEWS_PAGE_CONTEXT_VALUE: ReviewsPageContextType = DEFAULT_REVIEWS_PAGE_CONTEXT_BINDINGS;
